import React, { useContext, useEffect, useState, useRef } from 'react';
import { Table, Button, Form, Input, Typography, Popover, Tooltip } from 'antd';
import './styles.scss';
import { periodStatusColor } from '../../utils/periods';

const EditableContext = React.createContext(null);
const { Text } = Typography;

const CurrencyTable = (props) => {
    const columnsInitial = [
        {
            title: 'Currency',
            dataIndex: 'currency',
            key: 'currency',
            width: '150px',
            ellipsis: true,
            editable: true,
            render: (_, _currency) => {
                const currency = _currency.currency;

                return <Text style={{ marginLeft: 8 }}>{currency}</Text>;
            },
        },
    ];

    const setNewPeriodsCollapsed = (period, periods, expandedPeriods) => {
        let newPeriodsExpanded = [...expandedPeriods];

        for (let i = 0; i < periods.length; i++) {
            if (
                period.period_year === periods[i].period_year &&
                (periods[i].period_month !== '' || periods[i].period_quarter !== '')
            ) {
                newPeriodsExpanded[i] = false;
            }
        }
        props.handleSetExpandedPeriods(newPeriodsExpanded);
    };

    const setNewPeriodsExpanded = (period, isMonth) => {
        let newPeriodsExpanded = [...props.expandedPeriods];

        props.periods.forEach((p, index) => {
            let splitPeriod = p.period.split('-');

            if (!isMonth && splitPeriod.length === 2 && splitPeriod[0] === period.period && splitPeriod[1][0] === 'Q') {
                newPeriodsExpanded[index] = true;
            } else if (isMonth && splitPeriod.length === 2 && splitPeriod[0] === period.period) {
                newPeriodsExpanded[index] = true;
            }
        });
        props.handleSetExpandedPeriods(newPeriodsExpanded);
    };

    const renderExpansionButtons = (period) => {
        const currentPeriodIndex = props.periods.indexOf(period);
        const periodToExpand =
            props.periods[currentPeriodIndex - 1] != null ? props.periods[currentPeriodIndex - 1] : null;

        const showExpandMonth =
            periodToExpand != null
                ? periodToExpand.period_month !== ''
                : periodToExpand != null && periodToExpand.period_year === period.period_year
                  ? true
                  : null;

        const showExpandQuarter =
            periodToExpand != null
                ? periodToExpand.period_month === '' && periodToExpand.period_quarter !== ''
                : periodToExpand != null && periodToExpand.period_year === period.period_year
                  ? true
                  : null;

        const showCollapse = period != null ? period.period_month !== '' || period.period_quarter !== '' : false;

        return showExpandMonth || showExpandQuarter || showCollapse ? (
            <div>
                {showCollapse ? (
                    <Button
                        onClick={(e) => setNewPeriodsCollapsed(period, props.periods, props.expandedPeriods)}
                        type="text"
                    >
                        Collapse
                    </Button>
                ) : null}
                {showExpandMonth ? (
                    <Button onClick={() => setNewPeriodsExpanded(period, true)} type="text">
                        Expand to month
                    </Button>
                ) : null}
                {showExpandQuarter ? (
                    <Button onClick={() => setNewPeriodsExpanded(period, false)} type="text">
                        Expand to quarter
                    </Button>
                ) : null}
            </div>
        ) : null;
    };

    const periodColumn = (period, index) => {
    // Each period has its own status: a year can be split between actuals, budget and forecast.
    const statusColor = periodStatusColor(period);

        return {
            title: (
                <Popover content={renderExpansionButtons(period, index)} title="">
                    <span style={{ color: statusColor }}>{period.period}</span>
                </Popover>
            ),
            dataIndex: 'period' + period.period,
            key: 'value',
            width: '150px',
            ellipsis: true,
            editable: true,
            render: (value, currency) => {
                let currencyPeriods = currency.currency_period;
                let realIndex = 0;

                currencyPeriods.forEach((currency, index) => {
                    if (currency.period === period.period) {
                        realIndex = index;
                    }
                });
                const exchangeRate = currencyPeriods[realIndex].exchange_rate;

                return (
                    <Tooltip placement="top" title={<span>{exchangeRate}</span>}>
                        <span
                            style={{
                                textAlign: 'right',
                                float: 'right',
                                paddingRight: '15px',
                            }}
                        >
                            {exchangeRate}
                        </span>
                    </Tooltip>
                );
            },
        };
    };

    const createColumns = (columnsInitial, periods, expandedPeriods) => {
        let newPeriods = [];

        if (expandedPeriods != null && periods != null) {
            expandedPeriods.forEach((isPeriod, index) => {
                if (isPeriod && periods[index] != null) {
                    newPeriods.push(periods[index]);
                }
            });
        }
       return [...columnsInitial, ...newPeriods.map((p, i) => periodColumn(p, i))];
    };

    const EditableRow = ({ index, ...props }) => {
        const [form] = Form.useForm();

        return (
            <Form form={form} component={false}>
                <EditableContext.Provider value={form}>
                    <tr {...props} />
                </EditableContext.Provider>
            </Form>
        );
    };

    const EditableCell = ({ title, editable, children, dataIndex, currency, handleSave, testing, ...restProps }) => {
        const [editing, setEditing] = useState(false);
        const inputRef = useRef(null);
        const form = useContext(EditableContext);

        useEffect(() => {
            if (editing && inputRef && inputRef.current) {
                inputRef.current.focus();
            }
        }, [editing]);

        editable = typeof title !== 'undefined' ? true : false;

        let editValue = '';
        if (title === 'Currency' && currency != null) {
            editValue = currency.currency;
        } else if (currency != null) {
            currency.currency_period.forEach((currencyPeriod) => {
                if (currencyPeriod.period === dataIndex) {
                    editValue = currencyPeriod.exchange_rate;
                }
            });
        }

        const toggleEdit = () => {
            setEditing(!editing);
            form.setFieldsValue({
                [dataIndex]: editValue,
            });
        };

        const mountedRef = useRef(true);

            useEffect(() => {
            return () => {
                mountedRef.current = false;
            };
            }, []);

        const save = async () => {
            try {
                const exchangeRates = await form.validateFields();

                if (!mountedRef.current) return;

                toggleEdit();
                handleSave({ ...currency, period: { ...exchangeRates } });
            } catch (errInfo) {
                // console.log("Save failed:", errInfo);
            }
        };

        let childNode = children;
        if (editable) {
            childNode = editing ? (
                <Form.Item
                    style={{
                        margin: 0,
                    }}
                    name={dataIndex}
                    rules={[
                        {
                            required: true,
                            message: title === 'Currency' ? 'Ticker is required' : 'Exchange rate required',
                        },
                    ]}
                >
                    <Input ref={inputRef} onPressEnter={save} onBlur={save} />
                </Form.Item>
            ) : (
                <div
                    className="editable-cell-value-wrap"
                    style={{
                        height: 24,
                        width: 150,
                        padding: 0,
                    }}
                    onClick={toggleEdit}
                >
                    {children}
                </div>
            );
        }

        return <td {...restProps}>{childNode}</td>;
    };

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    const handleSave = (currency) => {
        if (props.handleUpdateCurrency) props.handleUpdateCurrency(currency);
    };

    const editableColumns = createColumns(columnsInitial, props.periods, props.expandedPeriods).map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (currency) => ({
                currency,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave: handleSave,
            }),
        };
    });

    const onSelectedRowKeysChange = (rowKey) => {
        const currencies = props.currency;
        const selectedRowKey = rowKey.pop();
        props.setSelectedRowKey([selectedRowKey]);

        let selectedCurrency = null;
        currencies.forEach((currency) => {
            if (currency.key === selectedRowKey) {
                selectedCurrency = currency;
            }
        });
        props.handleSetSelectedCurrency(selectedCurrency);
    };

    const rowSelection = {
        selectedRowKeys: props.selectedRowKey,
        onChange: onSelectedRowKeysChange,
    };

    return (
        <Table
            bordered
            rowSelection={{ hideSelectAll: true, ...rowSelection }}
            components={components}
            columns={editableColumns}
            dataSource={props.currency}
            pagination={false}
            size="small"
            className="tableFooter"
            scroll={{ x: 400 }}
        />
    );
};

export default CurrencyTable;
